import { selectionOf } from "./models";
/* Eval object model — four nouns and nothing else.

   Case      a test: an input, an optional expected output, some checks
   Check     a grader: runs against an output + trajectory, returns 0..1
   Run       an immutable record of every case scored against one agent version
   Result    one case inside one run

   The whole category (Braintrust, LangSmith, Langfuse, Phoenix, OpenAI Evals)
   converged on these four under different names. That convergence — not any one
   product's feature list — is the reason this shape is worth committing to.

   One rule carries most of the weight: SCORES ARE ALWAYS 0..1. A uniform range
   is what lets one delta column, one composite score and one regression verdict
   work across every check type. LangSmith allows bool/float/string and pays for
   it with a per-metric "higher is better" setting and no single grade.
   A null score means SKIPPED — excluded from averages and from regression math. */

import type {
  Agent, ConfigSnapshot, EvalCase, EvalCheck, EvalCheckType, EvalCheckTypeMeta,
  EvalSuite, EvalVerdict, MakeCaseOptions,
} from "@/shared/types";

export function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < String(s).length; i++) {
    h ^= String(s).charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/* -------------------------------- checks ---------------------------------- */

/* Three families. Output checks are table stakes. Trajectory checks are the
   agent-specific half — what it DID, not just what it said — and they are only
   cheap because buildSpans() already reconstructs the trajectory. Braintrust
   ships no prebuilt trajectory scorer at all; this is the asymmetry to press.

   Closed set on purpose. A user-supplied code scorer is the feature that rots
   an eval product: it can't be versioned, can't be trusted, can't be migrated. */
export const CHECK_TYPES: Record<EvalCheckType, EvalCheckTypeMeta> = {
  contains: {
    family: "output", label: "Contains", short: "Has", hint: "Output includes this text",
    fields: [{ key: "text", label: "Text", placeholder: "e.g. quarterly revenue" }],
    describe: (c) => `contains “${c.text || "…"}”`,
  },
  not_contains: {
    family: "output", label: "Must not contain", short: "Not", hint: "Output never includes this text",
    fields: [{ key: "text", label: "Text", placeholder: "e.g. I cannot" }],
    describe: (c) => `never says “${c.text || "…"}”`,
  },
  regex: {
    family: "output", label: "Matches pattern", short: "Regex", hint: "Output matches this regular expression",
    fields: [{ key: "pattern", label: "Pattern", placeholder: "e.g. \\$[0-9,]+" }],
    describe: (c) => `matches /${c.pattern || "…"}/`,
  },
  json_valid: {
    family: "output", label: "Valid JSON", short: "JSON", hint: "Output parses, and carries the required keys",
    fields: [{ key: "keys", label: "Required keys", placeholder: "comma separated — title, url" }],
    describe: (c) => (c.keys ? `JSON with ${c.keys}` : "valid JSON"),
  },
  exact: {
    family: "output", label: "Exact match", short: "Exact", hint: "Output equals the expected value",
    fields: [],
    describe: () => "exactly matches expected",
  },
  used_tool: {
    // Tool and arguments are separate dials on purpose — "right tool, wrong
    // arguments" is a distinct failure, and the argument half is the part
    // almost every eval tool leaves undocumented.
    family: "trajectory", label: "Used tool", short: "Tool", hint: "The agent called this tool — optionally with these arguments",
    fields: [
      { key: "tool", label: "Tool", placeholder: "e.g. web.search" },
      { key: "argMatch", label: "Arguments", options: ["subset", "exact", "ignore"] },
      { key: "args", label: "Expected arguments (JSON)", multiline: true, placeholder: '{"q": "Anthropic headcount"}' },
    ],
    describe: (c) => `calls ${c.tool || "…"}${c.args && c.argMatch !== "ignore" ? " with args" : ""}`,
  },
  never_tool: {
    family: "trajectory", label: "Never used tool", short: "No tool", hint: "The agent never called this tool — the guardrail check",
    fields: [{ key: "tool", label: "Tool", placeholder: "e.g. send_email" }],
    describe: (c) => `never calls ${c.tool || "…"}`,
  },
  tool_order: {
    family: "trajectory", label: "Tool order", short: "Order", hint: "Tools were called in this order",
    fields: [
      { key: "tools", label: "Tools", placeholder: "comma separated — search, read, write" },
      { key: "mode", label: "Match", options: ["subset", "strict"] },
    ],
    describe: (c) => `${c.mode === "strict" ? "exactly" : "in order"}: ${c.tools || "…"}`,
  },
  steps_under: {
    // Phoenix calls the ratio form of this "convergence" — partial credit for
    // getting there, just less directly than it should have.
    family: "trajectory", label: "Step budget", short: "Steps", hint: "Finished within this many steps — partial credit past it",
    fields: [{ key: "n", label: "Max steps", type: "number", placeholder: "8" }],
    describe: (c) => `≤ ${c.n || "?"} steps`,
  },
  judge: {
    family: "judgement", label: "LLM judge", short: "Judge", hint: "A model grades the output against a rubric, one criterion per line",
    fields: [
      { key: "rubric", label: "Rubric", multiline: true, placeholder: "Answers the question, cites a source, no hedging." },
      { key: "model", label: "Judge model", type: "model" },
    ],
    describe: (c) => (c.rubric ? `judge: ${String(c.rubric).slice(0, 40)}…` : "LLM judge"),
  },
  budget: {
    family: "judgement", label: "Budget", short: "Budget", hint: "Stayed inside a cost and time envelope",
    fields: [
      { key: "maxCostUsd", label: "Max cost ($)", type: "number", placeholder: "0.25" },
      { key: "maxMs", label: "Max duration (ms)", type: "number", placeholder: "60000" },
    ],
    describe: (c) =>
      [c.maxCostUsd && `≤ $${c.maxCostUsd}`, c.maxMs && `≤ ${Math.round((c.maxMs as number) / 1000)}s`]
        .filter(Boolean).join(" · ") || "within budget",
  },
};

export const CHECK_FAMILIES: [id: string, label: string, hint: string][] = [
  ["output", "Output", "What the agent said"],
  ["trajectory", "Trajectory", "What the agent did"],
  ["judgement", "Judgement", "Rubric and budget"],
];

let seq = 0;
const uid = (p: string) => `${p}-${Date.now().toString(36)}${(seq++).toString(36)}`;

export function makeCheck(type: EvalCheckType, config: Partial<EvalCheck> = {}): EvalCheck {
  return { id: uid("chk"), type, weight: 1, ...config };
}

export function makeCase(
  { name, input = "", expected = "", tags = [], checks = [], origin = null }: MakeCaseOptions = {},
): EvalCase {
  return {
    id: uid("case"),
    name: name || input.slice(0, 60) || "Untitled case",
    input,
    expected,
    tags,
    checks,
    origin,          // { runId, spanId } when promoted from a real trace
    created: "just now",
  };
}

/* One implicit suite per agent. No separate "datasets" collection: tags do the
   job of LangSmith splits / Phoenix splits, and a named-suite layer can be added
   later without touching the Case shape. That is the test this model has to pass. */
export function makeEvals(): EvalSuite {
  return { cases: [], checks: [], runs: [], baselineRunId: null };
}

/* Suite-wide checks apply to every case; case-local checks stack on top. */
export const checksFor = (evals: EvalSuite, kase: EvalCase): EvalCheck[] => [...(evals.checks || []), ...(kase.checks || [])];

export const allTags = (evals: EvalSuite): string[] =>
  [...new Set((evals.cases || []).flatMap((c) => c.tags || []))].sort();

/* ------------------------------ aggregation -------------------------------- */

/* Weighted mean over scored checks. null (skipped) is excluded rather than
   counted as zero — scoring a skip as a failure is how eval dashboards start
   lying to people. */
export function composite(checkResults: { score: number | null; weight?: number }[]): number | null {
  const scored = checkResults.filter((r): r is { score: number; weight?: number } => r.score != null);
  if (!scored.length) return null;
  const w = scored.reduce((n, r) => n + (r.weight || 1), 0);
  return +(scored.reduce((n, r) => n + r.score * (r.weight || 1), 0) / w).toFixed(3);
}

export function runScore(results: { score: number | null }[]): number | null {
  const scored = results.filter((r): r is { score: number } => r.score != null);
  if (!scored.length) return null;
  return +(scored.reduce((n, r) => n + r.score, 0) / scored.length).toFixed(3);
}

/* A case passes when every check that ran passed. Partial-credit checks
   (steps_under, json_valid, judge) count as passing at 0.7 and above — one
   threshold, stated once, rather than a per-check setting nobody will tune. */
export const PASS_AT = 0.7;
export const passed = (result: { score: number | null }): boolean => result.score != null && result.score >= PASS_AT;

/* Braintrust's four-way verdict. Needs the uniform 0..1 range to be computable
   at all, which is the whole argument for enforcing it. */
export function verdict(current: number | null | undefined, base: number | null | undefined): EvalVerdict {
  if (base == null || current == null) return "none";
  const d = +(current - base).toFixed(3);
  if (Math.abs(d) < 0.005) return "tie";
  return d > 0 ? "improvement" : "regression";
}

/* --------------------------- config snapshots ------------------------------ */

/* What an eval run is scored AGAINST, frozen at execution time. Without this a
   run's version chip is decorative and "v13 beat v12" is unauditable — see
   plans/2026-08-01-agent-evals.md §2. */
export function configSnapshot(agent: Agent): ConfigSnapshot {
  return {
    instructions: agent.instructions || "",
    model: agent.modelSettings?.model || agent.model,
    modelSelection: { ...selectionOf(agent) },
    temperature: agent.modelSettings?.temperature,
    maxTokens: agent.modelSettings?.maxTokens,
    connectedApps: [...(agent.connectedApps || [])],
    capabilities: { ...(agent.capabilities || {}) },
  };
}

/* Stable identity of a config. Two versions with identical config produce
   identical eval scores, which is exactly what you want: re-running an
   unchanged agent must not move the number. */
export const snapshotKey = (snap: ConfigSnapshot): string => hashStr(JSON.stringify(snap)).toString(36);

